/**
 * PWA Install Context — owns the install-prompt lifecycle for LifeGuide.
 *
 * Responsibilities:
 *   • Register the service worker.
 *   • Capture Android's `beforeinstallprompt` event once, store the deferred
 *     prompt, and expose a `promptInstall()` method.
 *   • Detect iOS Safari (where no browser prompt exists).
 *   • Detect current "standalone" launch mode (iOS + Android).
 *   • Expose `maybeShowSaveToPhone("guidance_complete" | "say_it_better" | …)`
 *     — fires the install affordance only after a meaningful interaction, at
 *     most once per visit, and never if the user already dismissed or installed.
 *   • Fire a single `pwa:save-prompt-shown` custom event when the affordance
 *     shows, so analytics listeners can log it without reaching into context.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const Ctx = createContext(null);

const SESSION_FLAG = "lg_pwa_prompt_shown_this_visit";
const PERSIST_FLAG = "lg_pwa_installed";
const DISMISS_FLAG = "lg_pwa_dismissed_until";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}
function detectStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export function PwaInstallProvider({ children }) {
  const [deferred, setDeferred] = useState(null);
  const [standalone, setStandalone] = useState(detectStandalone);
  const [promptKind, setPromptKind] = useState(null); // "android" | "ios" | null
  const [promptTrigger, setPromptTrigger] = useState(null); // "guidance_complete" | "say_it_better" | "manual"
  const triggeredThisVisitRef = useRef(false);

  // 1) Register service worker (production + preview only, not localhost HMR).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") return;
    const register = () => {
      navigator.serviceWorker.register("/service-worker.js").catch(() => {});
    };
    if (document.readyState === "complete") register();
    else window.addEventListener("load", register, { once: true });
  }, []);

  // 2) Capture Android's deferred prompt.
  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      try { localStorage.setItem(PERSIST_FLAG, "1"); } catch {}
      setDeferred(null);
      // Fire analytics directly here so Android installs always get recorded,
      // even when the user accepts the UA prompt (not our modal).
      window.dispatchEvent(new CustomEvent("pwa:install-accepted"));
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // 3) Track standalone launches.
  useEffect(() => {
    const mql = window.matchMedia?.("(display-mode: standalone)");
    if (!mql) return;
    const h = () => setStandalone(detectStandalone());
    mql.addEventListener?.("change", h);
    return () => mql.removeEventListener?.("change", h);
  }, []);

  // 4) Emit "launched in standalone" exactly once per standalone session.
  const standaloneEmittedRef = useRef(false);
  useEffect(() => {
    if (!standalone || standaloneEmittedRef.current) return;
    standaloneEmittedRef.current = true;
    window.dispatchEvent(new CustomEvent("pwa:standalone-launch"));
  }, [standalone]);

  const canPromptAndroid = !!deferred;
  const canShowIos = isIOS() && !standalone;

  // 5) Public: show the affordance after a meaningful event.
  const maybeShowSaveToPhone = useCallback((trigger = "manual") => {
    if (triggeredThisVisitRef.current) return;
    if (standalone) return;
    try {
      if (localStorage.getItem(PERSIST_FLAG) === "1") return;
      if (sessionStorage.getItem(SESSION_FLAG) === "1") return;
      const dismissedUntil = parseInt(localStorage.getItem(DISMISS_FLAG) || "0", 10);
      if (dismissedUntil && Date.now() < dismissedUntil) return;
    } catch {}

    if (!canPromptAndroid && !canShowIos) return; // nothing we can offer here
    triggeredThisVisitRef.current = true;
    try { sessionStorage.setItem(SESSION_FLAG, "1"); } catch {}
    setPromptKind(canPromptAndroid ? "android" : "ios");
    setPromptTrigger(trigger);
    window.dispatchEvent(new CustomEvent("pwa:save-prompt-shown", {
      detail: { kind: canPromptAndroid ? "android" : "ios", trigger },
    }));
  }, [canPromptAndroid, canShowIos, standalone]);

  // 6) Public: trigger the Android native prompt.
  const promptInstall = useCallback(async () => {
    window.dispatchEvent(new CustomEvent("pwa:save-prompt-clicked", {
      detail: { kind: "android", trigger: promptTrigger || "manual" },
    }));
    if (!deferred) return { outcome: "unavailable" };
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        try { localStorage.setItem(PERSIST_FLAG, "1"); } catch {}
        window.dispatchEvent(new CustomEvent("pwa:install-accepted", { detail: { trigger: promptTrigger } }));
      } else {
        window.dispatchEvent(new CustomEvent("pwa:install-dismissed", { detail: { trigger: promptTrigger } }));
      }
      setDeferred(null);
      return choice;
    } catch (e) {
      return { outcome: "error" };
    } finally {
      setPromptKind(null);
    }
  }, [deferred, promptTrigger]);

  // 7) Open the panel manually (footer / menu entry).
  const openManually = useCallback(() => {
    if (standalone) return;
    setPromptKind(isIOS() ? "ios" : "android");
    setPromptTrigger("manual");
    window.dispatchEvent(new CustomEvent("pwa:save-prompt-shown", {
      detail: { kind: isIOS() ? "ios" : "android", trigger: "manual" },
    }));
    if (isIOS()) {
      window.dispatchEvent(new CustomEvent("pwa:ios-instructions-shown", {
        detail: { trigger: "manual" },
      }));
    }
  }, [standalone]);

  // 7b) Allow deeply-nested components (e.g. GuestPage > SayItBetterPanel)
  // to request the prompt without plumbing props through every layer.
  useEffect(() => {
    const onReq = (e) => maybeShowSaveToPhone(e?.detail?.trigger || "manual");
    window.addEventListener("pwa:request-save-prompt", onReq);
    return () => window.removeEventListener("pwa:request-save-prompt", onReq);
  }, [maybeShowSaveToPhone]);

  const dismissPrompt = useCallback((hideDays = 0) => {
    setPromptKind(null);
    try {
      if (hideDays > 0) {
        localStorage.setItem(DISMISS_FLAG, String(Date.now() + hideDays * 86400 * 1000));
      }
    } catch {}
    window.dispatchEvent(new CustomEvent("pwa:save-prompt-dismissed", {
      detail: { trigger: promptTrigger || "manual", hideDays },
    }));
  }, [promptTrigger]);

  const value = useMemo(
    () => ({
      canInstall: canPromptAndroid || canShowIos,
      canPromptAndroid,
      canShowIos,
      standalone,
      promptKind,
      promptTrigger,
      maybeShowSaveToPhone,
      promptInstall,
      openManually,
      dismissPrompt,
    }),
    [canPromptAndroid, canShowIos, standalone, promptKind, promptTrigger, maybeShowSaveToPhone, promptInstall, openManually, dismissPrompt]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePwaInstall() {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePwaInstall must be used inside <PwaInstallProvider>");
  return v;
}
